import { StatusCodes } from 'http-status-codes'
import { toRefs, reactive } from 'vue'

import { apiClient } from '@/api/api-client/ApiClient'
import { logApiEvent, sanitizeData } from '@/utils/sentry-logger'

export const useFetch = (url, options) => {
  const state = reactive({
    response: [],
    headers: {},
    error: null,
    fetching: false,
  })
  const fetchData = async (params) => {
    state.fetching = true
    state.error = null
    const requestStartTime = Date.now()
    try {
      const body = JSON.stringify(params.body)
      const res = await apiClient.fetch(url, {
        ...options,
        ...params,
        body,
      })
      state.headers = Object.fromEntries(res.headers.entries())

      if (res.status === StatusCodes.NO_CONTENT) {
        state.response.status = StatusCodes.NO_CONTENT
        logApiEvent(
          url,
          options?.method || params?.method || 'GET',
          {
            requestPayload: sanitizeData(params?.body),
            statusCode: res.status,
            responseTime: Date.now() - requestStartTime,
            timestamp: new Date().toISOString(),
          },
          'success'
        )
        return
      }

      if (res.status === StatusCodes.SERVICE_UNAVAILABLE) {
        state.response.status = StatusCodes.SERVICE_UNAVAILABLE
        logApiEvent(
          url,
          options?.method || params?.method || 'GET',
          {
            requestPayload: sanitizeData(params?.body),
            statusCode: res.status,
            responseTime: Date.now() - requestStartTime,
            timestamp: new Date().toISOString(),
          },
          'info'
        )
        return
      }

      if (res.status === StatusCodes.ACCEPTED) {
        state.response.status = StatusCodes.ACCEPTED
        logApiEvent(
          url,
          options?.method || params?.method || 'GET',
          {
            requestPayload: sanitizeData(params?.body),
            statusCode: res.status,
            responseTime: Date.now() - requestStartTime,
            timestamp: new Date().toISOString(),
          },
          'success'
        )
        return
      }

      const json = await res.json()
      if (res.status === StatusCodes.OK) {
        state.response = json

        // Log successful requests with payload and response
        logApiEvent(
          url,
          options?.method || params?.method || 'GET',
          {
            requestPayload: sanitizeData(params?.body),
            serverResponse: sanitizeData(json),
            statusCode: res.status,
            statusText: res.statusText,
            responseTime: Date.now() - requestStartTime,
            timestamp: new Date().toISOString(),
          },
          'success'
        )
        return
      }

      state.error = json.message

      logApiEvent(
        url,
        options?.method || params?.method || 'GET',
        {
          errorMessage: json.message || 'API error',
          backendException: {
            type: json.exception || null,
            file: json.file || null,
            line: json.line || null,
            message: json.message || null,
            trace: json.trace?.slice?.(0, 5) || null,
          },
          requestPayload: sanitizeData(params?.body),
          serverResponse: json,
          statusCode: res.status,
          statusText: res.statusText,
          responseTime: Date.now() - requestStartTime,
          timestamp: new Date().toISOString(),
        },
        'failure'
      )
    } catch (error) {
      state.error = error

      logApiEvent(
        url,
        options?.method || params?.method || 'GET',
        {
          errorMessage: error.message || 'Network or unexpected error',
          errorName: error.name,
          errorStack: error.stack?.substring?.(0, 500),
          requestPayload: sanitizeData(params?.body),
          statusCode: error.status || error.code,
          responseTime: Date.now() - requestStartTime,
          timestamp: new Date().toISOString(),
        },
        'failure'
      )
    } finally {
      state.fetching = false
    }
  }
  return { ...toRefs(state), fetchData }
}
